import { useFormikContext } from 'formik';
import React from 'react';
import { InputLabel, Poppins, Spacer } from 'src/common';
import styled from 'styled-components';
import MaterialInput from '../../form/MaterialInput';
import MaterialDatePicker from '../../form/MaterialDatePicker';
import Button from '../../form/Button';
import CurrencyInput, { Symbol } from 'src/components/form/CurrencyInput';
import { StrategySelect, ControlStatusSelect } from 'src/components/select';
import UserInput from 'src/components/UserInput';
import { getCurrencySymbol } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { ControlInput, MaturityLevel } from 'src/graphql/_generated';
import { MaturitySelect } from 'src/components/select';
import { FrameworkControlsManager } from './FrameworkControlsManager';

const Div = styled.div`
  .grid {
    display: grid;
    grid-gap: 21px;

    &--2 {
      grid-template-columns: 1fr 1fr;
    }
    &--3 {
      grid-template-columns: 1fr 1fr 1fr;
    }
    &--2-1 {
      grid-template-columns: 2fr 1fr;
    }
  }
`;

interface RmpFormProps {
  buttonTitle: string;
  onSubmitCb?: () => void;
}

const RmpForm: React.FC<RmpFormProps> = ({ buttonTitle, onSubmitCb }) => {
  const { features } = useAuth();
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    setFieldValue,
    touched,
    setFieldTouched,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
    status,
  } = useFormikContext<ControlInput>();

  const frameworkControlsValid = status?.frameworkControlsValid ?? false;
  const disabled = !isValid || !frameworkControlsValid || isSubmitting || !dirty;

  return (
    <Div>
      <div className="h-padding">
        {/* BASIC DETAILS */}
        <>
          <Poppins weight={600}>BASIC DETAILS:</Poppins>
          <Spacer $px={20} />
          <div>
            <InputLabel>NAME *</InputLabel>
            <AutoGrowingInput
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter a name"
              css="width: 100%;"
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              data-cy="name-input"
            />
          </div>
          <Spacer $px={20} />

          <InputLabel>DESCRIPTION</InputLabel>
          <MaterialInput
            name="description"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your text"
            css="width: 100%;"
            error={touched.description && Boolean(errors.description)}
            helperText={touched.description && errors.description}
            multiline
            minRows={3}
            maxRows={10}
            data-cy="description-input"
          />
        </>
        {/* FRAMEWORK */}
        <Spacer $px={34} />
        <FrameworkControlsManager />

        {/* Strategy & Ownership */}
        <Spacer $px={34} />
        <>
          <Poppins weight={600}>STRATEGY & OWNERSHIP:</Poppins>
          <Spacer $px={20} />
          <div className="grid grid--3">
            <div>
              <InputLabel>STRATEGY</InputLabel>
              <StrategySelect
                onChange={(o) => setFieldValue('strategy', o.value)}
                onBlur={() => setFieldTouched('strategy', true)}
                value={values.strategy}
                error={touched.strategy && Boolean(errors.strategy)}
                helperText={touched.strategy && errors.strategy}
              />
            </div>
            <div>
              <InputLabel>OWNER</InputLabel>
              <UserInput
                value={values.owner || { text: '' }}
                onChange={(usr) => {
                  const { __typename, ...cleanUsr } = usr || {};
                  setFieldValue('owner', cleanUsr);
                }}
                onSelectUser={(usr) => {
                  const { __typename, ...cleanUsr } = usr || {};
                  setFieldValue('owner', cleanUsr);
                }}
              />
            </div>
            <div>
              <InputLabel>STATUS</InputLabel>
              <ControlStatusSelect
                onChange={(o) => setFieldValue('status', o.value)}
                onBlur={() => setFieldTouched('status', true)}
                value={values.status}
                error={touched.status && Boolean(errors.status)}
                helperText={touched.status && errors.status}
              />
            </div>
          </div>
        </>
        {/* Maturity & Lifecycle */}
        <Spacer $px={34} />
        <>
          <Poppins weight={600}>MATURITY & LIFECYCLE:</Poppins>
          <Spacer $px={20} />
          <div className="grid grid--3">
            <div>
              <InputLabel>EXISTING MATURITY</InputLabel>
              <MaturitySelect
                name="existingMaturity"
                onChange={(o: { value: MaturityLevel }) => setFieldValue('existingMaturity', o.value)}
                onBlur={() => setFieldTouched('existingMaturity', true)}
                value={values.existingMaturity}
                error={touched.existingMaturity && Boolean(errors.existingMaturity)}
                inputId="existing-maturity-select"
              />
              {touched.existingMaturity && errors.existingMaturity && (
                <span className="helper-text-err">{errors.existingMaturity}</span>
              )}
            </div>
            <div>
              <InputLabel>TARGET MATURITY</InputLabel>
              <MaturitySelect
                name="targetMaturity"
                onChange={(o: { value: MaturityLevel }) => setFieldValue('targetMaturity', o.value)}
                onBlur={() => setFieldTouched('targetMaturity', true)}
                value={values.targetMaturity}
                error={touched.targetMaturity && Boolean(errors.targetMaturity)}
                inputId="target-maturity-select"
              />
              {touched.targetMaturity && errors.targetMaturity && (
                <span className="helper-text-err">{errors.existingMaturity}</span>
              )}
            </div>
          </div>
          <Spacer $px={20} />
          <div className="grid grid--3">
            <div>
              <InputLabel>EXPECTED LIVE DATE</InputLabel>
              <MaterialDatePicker
                value={values.expectedActiveDate}
                onChange={(date) => setFieldValue('expectedActiveDate', date)}
                inputProps={{
                  placeholder: 'Expected live date',
                  onBlur: handleBlur('expected_active_date'),
                  fullWidth: true,
                  error: touched.expectedActiveDate && Boolean(errors.expectedActiveDate),
                  helperText: touched.expectedActiveDate && (errors.expectedActiveDate as string),
                  ['data-cy']: 'expected_active_date-input',
                }}
              />
            </div>
            <div>
              <InputLabel>ACTUAL LIVE DATE</InputLabel>
              <MaterialDatePicker
                value={values.actualActiveDate}
                onChange={(date) => setFieldValue('actualActiveDate', date)}
                inputProps={{
                  placeholder: 'Actual live date',
                  onBlur: handleBlur('actual_active_date'),
                  error: touched.actualActiveDate && Boolean(errors.actualActiveDate),
                  helperText: touched.actualActiveDate && (errors.actualActiveDate as string),
                  fullWidth: true,
                  ['data-cy']: 'actual_active_date-input',
                }}
              />
            </div>
            <div>
              <InputLabel>REVIEW DATE</InputLabel>
              <MaterialDatePicker
                value={values.reviewDate}
                onChange={(date) => setFieldValue('reviewDate', date)}
                inputProps={{
                  placeholder: 'Review date',
                  onBlur: handleBlur('reviewDate'),
                  error: touched.reviewDate && Boolean(errors.reviewDate),
                  helperText: touched.reviewDate && (errors.reviewDate as string),
                  fullWidth: true,
                  ['data-cy']: 'review_date-input',
                }}
              />
            </div>
          </div>
        </>
        {/* Cost & Vendor */}
        <Spacer $px={34} />
        <>
          <Poppins weight={600}>COST & VENDOR:</Poppins>
          <Spacer $px={20} />
          <div className="grid grid--3">
            <div>
              <InputLabel>ANNUAL COST</InputLabel>
              <CurrencyInput
                id="annual_cost"
                InputProps={{ startAdornment: <Symbol>{getCurrencySymbol()}</Symbol> }}
                value={values.annualCost}
                onValueChange={({ floatValue }) => setFieldValue(`annualCost`, floatValue || '')}
                onBlur={handleBlur}
                error={touched.annualCost && Boolean(errors.annualCost)}
                helperText={touched.annualCost && errors.annualCost}
                placeholder="Annual cost"
                data-cy="annual_cost-input"
              />
            </div>
            <div>
              <InputLabel>VENDOR</InputLabel>
              <AutoGrowingInput
                name="vendor"
                value={values.vendor}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Vendor"
                css="width: 100%;"
                error={touched.vendor && Boolean(errors.vendor)}
                helperText={touched.vendor && errors.vendor}
                data-cy="vendor-input"
              />
            </div>
          </div>
        </>
        {/* EXTERNAL REFERENCE */}
        <Spacer $px={34} />
        <>
          <Poppins weight={600}>EXTERNAL REFERENCE:</Poppins>
          <Spacer $px={20} />
          <div className="grid grid--3">
            <div>
              <InputLabel>LINK URL</InputLabel>
              <AutoGrowingInput
                name="linkUrl"
                value={values.linkUrl || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter URL"
                css="width: 100%;"
                error={touched.linkUrl && Boolean(errors.linkUrl)}
                helperText={touched.linkUrl && errors.linkUrl}
                data-cy="url-input"
              />
            </div>
            <div>
              <InputLabel>LINK TEXT</InputLabel>
              <AutoGrowingInput
                name="linkText"
                value={values.linkText || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter a name for the URL"
                css="width: 100%;"
                error={touched.linkText && Boolean(errors.linkText)}
                helperText={touched.linkText && errors.linkText}
                data-cy="url_name-input"
              />
            </div>
          </div>
        </>
        <Spacer $px={50} />
        <Button
          disabled={disabled}
          onClick={() => {
            handleSubmit();
            onSubmitCb && onSubmitCb();
          }}
          primary
          css="width: 100%;"
          data-cy="submit"
        >
          {buttonTitle}
        </Button>
      </div>
    </Div>
  );
};

export default RmpForm;
